/**
 * Whole-database export and import.
 *
 * Ben should never feel locked in, and he wants a backup before the move. The
 * export is a single JSON file containing every record plus settings, and the
 * import takes that file back.
 *
 * Import is MERGE-BY-DEFAULT, not replace. Restoring a backup onto a phone
 * that has since logged new meals must not delete them. `mode: 'replace'`
 * exists for a deliberate wipe-and-restore and says so at the call site.
 */
import { DEFAULT_SETTINGS, SCHEMA_VERSION, type CheckIn, type DailyMetrics, type Reading, type Settings, type StoreName } from './schema';
import { mergeDay } from './biometrics';
import { STORE_NAMES, clear, getAll, metaGet, metaSet, putMany, update } from './db';
import { nowIso } from '../lib/time';

export interface ExportFile {
  format: 'reta-export';
  schemaVersion: number;
  exportedAt: string;
  appVersion: string;
  settings: Settings;
  counts: Record<string, number>;
  records: Record<string, unknown[]>;
}

export const SETTINGS_KEY = 'settings';

export async function loadSettings(): Promise<Settings> {
  const stored = await metaGet<Settings>(SETTINGS_KEY);
  return { ...DEFAULT_SETTINGS, ...(stored ?? {}) };
}

export async function saveSettings(settings: Settings): Promise<void> {
  await metaSet(SETTINGS_KEY, { ...settings, updatedAt: nowIso() });
}

/** Build the full export payload. */
export async function exportAll(appVersion = '1.0.0'): Promise<ExportFile> {
  const records: Record<string, unknown[]> = {};
  const counts: Record<string, number> = {};
  for (const store of STORE_NAMES) {
    // Raw sync payloads are debugging scratch, not user data; excluding them
    // keeps the backup small and free of duplicated health blobs.
    if (store === 'raw') continue;
    const rows = await getAll(store);
    records[store] = rows;
    counts[store] = rows.length;
  }
  return {
    format: 'reta-export',
    schemaVersion: SCHEMA_VERSION,
    exportedAt: nowIso(),
    appVersion,
    settings: await loadSettings(),
    counts,
    records,
  };
}

/** Serialise an export to a downloadable Blob. */
export function toBlob(file: ExportFile): Blob {
  return new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' });
}

/** A filename that sorts chronologically. */
export function exportFilename(at: Date = new Date()): string {
  const stamp = at.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `reta-backup-${stamp}.json`;
}

export interface ImportResult {
  ok: boolean;
  imported: Record<string, number>;
  skipped: string[];
  errors: string[];
}

/** Validate that an unknown object looks like one of our exports. */
export function validateExport(value: unknown): { ok: true; file: ExportFile } | { ok: false; error: string } {
  if (!value || typeof value !== 'object') return { ok: false, error: 'File is not a JSON object.' };
  const f = value as Partial<ExportFile>;
  if (f.format !== 'reta-export') return { ok: false, error: 'Not a Reta export file.' };
  if (typeof f.schemaVersion !== 'number') return { ok: false, error: 'Export is missing a schema version.' };
  if (f.schemaVersion > SCHEMA_VERSION) {
    return { ok: false, error: `Export was made by a newer version of Reta (schema ${f.schemaVersion}, this app reads ${SCHEMA_VERSION}).` };
  }
  if (!f.records || typeof f.records !== 'object') return { ok: false, error: 'Export contains no records.' };
  return { ok: true, file: f as ExportFile };
}

/**
 * Import an export file.
 *
 * `merge` (default) writes every record by key, so a record present in both
 * the file and the device is overwritten by the file's copy while anything
 * only on the device is left alone. `replace` clears each store first.
 */
export async function importAll(
  value: unknown, mode: 'merge' | 'replace' = 'merge',
): Promise<ImportResult> {
  const result: ImportResult = { ok: false, imported: {}, skipped: [], errors: [] };
  const check = validateExport(value);
  if (!check.ok) {
    result.errors.push(check.error);
    return result;
  }
  const file = check.file;

  for (const [store, rows] of Object.entries(file.records)) {
    if (!STORE_NAMES.includes(store as StoreName)) {
      result.skipped.push(store);
      continue;
    }
    if (!Array.isArray(rows)) {
      result.errors.push(`Store ${store} was not an array.`);
      continue;
    }
    try {
      if (mode === 'replace') await clear(store as StoreName);
      if (mode === 'merge' && store === 'metrics') {
        // Field-by-field with source precedence, never row-replace: a day the
        // device has since filled from Oura must keep those readings.
        for (const row of rows as DailyMetrics[]) {
          const { day, updatedAt, ...fields } = row;
          void updatedAt;
          await update('metrics', day, (cur) => mergeDay(cur, day, fields as unknown as Record<string, Reading>));
        }
      } else if (mode === 'merge' && store === 'checkins') {
        for (const row of rows as CheckIn[]) {
          await update('checkins', row.day, (cur) => ({ ...(cur ?? {}), ...row }));
        }
      } else {
        await putMany(store as StoreName, rows as never[]);
      }
      result.imported[store] = rows.length;
    } catch (err) {
      result.errors.push(`${store}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  if (file.settings) {
    try {
      await saveSettings({ ...DEFAULT_SETTINGS, ...file.settings });
    } catch (err) {
      result.errors.push(`settings: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  result.ok = result.errors.length === 0;
  return result;
}

/** Wipe every store. Used by the "delete all data" control in Settings. */
export async function wipeAll(): Promise<void> {
  for (const store of STORE_NAMES) await clear(store);
  await clear('meta');
}
